import { MultiDeviceMetricsTable } from './multi-device-metrics-table.tsx';
import { tableColumns } from './multi-device-metrics-table.columns.tsx';
import { MultiDeviceMetricsTableRowSubComponent } from './multi-device-metrics-table-row-sub-component.tsx';
import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import {
  DEVICE_TYPES_NAMES_ARRAY,
  DeviceTypeCodes,
  DeviceTypeNames,
} from '@/features/devices/schemas/devices.schemas.ts';
import { cn } from '@/utils/utils.ts';
import { useCallback, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { Button } from '@/components/ui/button.tsx';
import { useSession } from '@/services/providers/use-session.ts';
import { PlantDeviceFields } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-devices-overview.ts';
import { PlantFieldsPickerDialog } from '@/features/plants/components/multi-device-metrics-table/plant-fields-picker-dialog.tsx';

type MultiDeviceMetricsTableCardProps = {
  data: PlantDeviceFields[] | undefined;
  config?: Partial<Record<DeviceTypeCodes, number[]>>;
  type: 'production' | 'storage';
  plantId: string;
  deviceTypes?: DeviceTypeNames[];
  title: string;
  isLoading: boolean;
  expandable?: {
    isExpandable: boolean;
    defaultExpanded: boolean;
    onExpandChange?: (isExpanded: boolean) => void;
  };
  showSelectFields?: boolean;
  className?: string;
};

export const MultiDeviceMetricsTableCard = ({
  data,
  config,
  type,
  plantId,
  deviceTypes = DEVICE_TYPES_NAMES_ARRAY,
  title,
  isLoading,
  expandable = {
    isExpandable: false,
    defaultExpanded: true,
  },
  showSelectFields = false,
  className,
}: MultiDeviceMetricsTableCardProps) => {
  const [isExpanded, setIsExpanded] = useState(expandable.defaultExpanded);
  const cardRef = useRef<HTMLDivElement>(null);

  const { role } = useSession();

  const handleClick = useCallback(() => {
    setIsExpanded((prev) => {
      const newValue = !prev;
      expandable.onExpandChange?.(newValue);
      return newValue;
    });
    setTimeout(() => {
      if (cardRef.current) {
        cardRef.current.focus();
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }, [expandable]);

  return (
    <DashboardCard
      className={cn(isExpanded && 'min-h-[464px]', className)}
      isLoading={isLoading}
    >
      <div ref={cardRef}>
        <div>
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-xl font-semibold text-nowrap">{title}</h3>
            <div>
              {showSelectFields && (
                <PlantFieldsPickerDialog
                  title={title}
                  plantId={plantId}
                  config={config}
                  deviceTypes={deviceTypes}
                  type={type}
                />
              )}
              {expandable.isExpandable && (
                <Button variant="secondary" className="p-2" onClick={handleClick}>
                  {isExpanded ? (
                    <ChevronUpIcon className="size-5" />
                  ) : (
                    <ChevronDownIcon className="size-5" />
                  )}
                </Button>
              )}
            </div>
          </div>
          <div
            className={cn(
              'grid overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
          >
            {deviceTypes && deviceTypes.length > 0 && isExpanded && (
              <MultiDeviceMetricsTable
                data={data}
                columns={tableColumns(role)}
                getRowCanExpand={() => true}
                RenderSubComponent={MultiDeviceMetricsTableRowSubComponent}
                plantId={plantId}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
      </div>
    </DashboardCard>
  );
};
